import React, { useEffect, useState } from "react";
import { Grid, Typography, Box } from "@mui/material";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  ResponsiveContainer, Legend, LineChart, Line,
} from "recharts";
import DashboardCard from "../DashboardCard";

const COLORS = ["#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

// Dummy data
const sampleData = {
  payroll_this_month: "₹12.5L",
  tds_collected: "₹1.8L",
  pf_contributions: "₹90K",
  payslips_generated: 112,
  pending_reimbursements: 4,

  // Monthly payroll expense trend
  payroll_trend: [
    { month: "Apr", amount: 1150000 },
    { month: "May", amount: 1220000 },
    { month: "Jun", amount: 1180000 },
    { month: "Jul", amount: 1260000 },
    { month: "Aug", amount: 1250000 },
  ],

  // Pie chart: TDS vs PF
  tax_split: [
    { name: "TDS", value: 180000 },
    { name: "PF", value: 90000 },
  ],

  // Payslip generation (last 4 months)
  payslip_status: [
    { month: "Jun", generated: 110 },
    { month: "Jul", generated: 111 },
    { month: "Aug", generated: 112 },
    { month: "Sep", generated: 105 },
  ],

  // Reimbursement status
  reimbursements: [
    { name: "Approved", value: 12 },
    { name: "Pending", value: 4 },
    { name: "Rejected", value: 2 },
  ],
};

const FinanceDashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(sampleData);
  }, []);

  if (!data) return <div>Loading...</div>;

  return (
    <>
      <Typography variant="h4" gutterBottom>Finance Dashboard</Typography>
      <Grid container spacing={2} mb={4}>
        <DashboardCard title="Payroll This Month" value={data.payroll_this_month} />
        <DashboardCard title="TDS Collected" value={data.tds_collected} />
        <DashboardCard title="PF Contributions" value={data.pf_contributions} />
        <DashboardCard title="Payslips Generated" value={data.payslips_generated} />
        <DashboardCard title="Pending Reimbursements" value={data.pending_reimbursements} />
      </Grid>

      <Box mt={4}>
        <Grid container spacing={4}>

          {/* Line Chart - Payroll Trend */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Monthly Payroll Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.payroll_trend}>
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `₹${v / 100000}L`} />
                <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                <Line type="monotone" dataKey="amount" stroke="#8884d8" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </Grid>

          {/* Pie Chart - Tax Distribution */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">TDS vs PF Contributions</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.tax_split}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {data.tax_split.map((entry, index) => (
                    <Cell key={`cell-tax-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Grid>

          {/* Bar Chart - Payslip Generation */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Payslip Generation (Last 4 Months)</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.payslip_status}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="generated" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </Grid>

          {/* Pie Chart - Reimbursement Status */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Reimbursement Status</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.reimbursements}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {data.reimbursements.map((entry, index) => (
                    <Cell key={`cell-reimb-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Grid>

        </Grid>
      </Box>
    </>
  );
};

export default FinanceDashboard;
